import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Archive, ArchiveRestore, Check, ChevronRight, CookingPot, Plus, Search, Star, Trash2, X } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Button, BotonEnlace } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Casilla } from '@/components/Casilla';
import { EstadoVacio } from '@/components/Card';
import { NumberField, TextField, clasesCon, limpiarDecimal } from '@/components/Field';
import { archivarPlatillo, borrarPlatillo, guardarPlatillo, leerPlatillo, leerPlatillos } from '@/db/platillos';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { Dish, EstadoCoccion, Food, IngredientePlatillo, Nutrientes } from '@/db/types';
import { ahoraIso } from '@/domain/dates';
import { formatearNumero, leerDecimal } from '@/domain/numbers';
import { buscarAlimentos, type Candidato, type Uso } from '@/domain/busqueda';
import {
  GRAMOS,
  calcular,
  estadoDe,
  formatearGramos,
  formatearKcal,
  opcionesDePorcion,
  textoMacros,
} from '@/domain/nutricion';
import {
  MAXIMO_INGREDIENTES,
  calcularPlatillo,
  pesoFinalDe,
  por100gDePlatillo,
  type CalculoPlatillo,
  type ResolverIngrediente,
} from '@/domain/platillos';
import type { EstadoBase } from '@/lib/useAlimentosBase';
import { useFechaHoy } from '@/lib/useFechaHoy';
import { Etiqueta, EtiquetaEstado } from './comunes';
import { kcalPorRacion, textoRaciones, useResolverIngredientes } from './ingredientes';

/**
 * Mis platillos (Req. 20.3 a 20.6; compuerta de la Fase 1, punto 13): recetas
 * con ingredientes de la base o de Mis alimentos, las raciones que rinden y,
 * si se pesan ya cocinadas, su peso final para registrarlas por gramos.
 */
export function PlatillosPage() {
  const platillos = useLiveQuery(() => leerPlatillos(), []);
  const { base, propios, resolver } = useResolverIngredientes();
  const [verArchivados, setVerArchivados] = useState(false);
  if (platillos === undefined || propios === undefined || base.estado === 'cargando') return null;
  const activos = platillos.filter((p) => !p.archivado);
  const archivados = platillos.filter((p) => p.archivado);

  const detalle = (p: Dish) =>
    resolver === null ? textoRaciones(p.raciones) : `${textoRaciones(p.raciones)} · ${kcalPorRacion(p, resolver)}`;

  const lista = (items: readonly Dish[]) => (
    <ul className="divide-y divide-borde">
      {items.map((p) => (
        <li key={p.id}>
          <Link to={`/comida/platillos/${p.id}`} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
            <span className="grid min-w-0 flex-1 gap-1">
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="text-[14px] font-medium text-texto">{p.nombre}</span>
                {p.favorito && (
                  <>
                    <Star aria-hidden="true" className="size-3.5 fill-current text-marca" />
                    <span className="sr-only">Favorito</span>
                  </>
                )}
              </span>
              <span className="text-[12px] text-texto-suave">{detalle(p)}</span>
            </span>
            <ChevronRight className="size-4 text-texto-suave" />
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <PageHeader
        titulo="Mis platillos."
        descripcion="Tus recetas con sus ingredientes, para registrarlas por ración o, si las pesas ya cocinadas, por gramos."
        volver={{ a: '/comida', texto: 'Comida', etiqueta: 'Volver a Comida' }}
      />
      <BotonEnlace to="/comida/platillos/nuevo" icono={CookingPot}>
        Nuevo platillo
      </BotonEnlace>

      {base.estado === 'error' && (
        <div className="mt-5">
          <AvisoBaseSinAbrir />
        </div>
      )}

      <div className="mt-6">
        {activos.length === 0 ? (
          <EstadoVacio
            icono={CookingPot}
            titulo="Todavía no tienes platillos."
            descripcion="Arma uno con ingredientes de la base o de Mis alimentos y las raciones que rinde. Cambiarlo después no cambia lo que ya registraste."
          />
        ) : (
          lista(activos)
        )}
      </div>

      {archivados.length > 0 && (
        <section className="mt-6">
          <button
            type="button"
            aria-expanded={verArchivados}
            onClick={() => setVerArchivados((v) => !v)}
            className="inline-flex min-h-11 items-center gap-2 text-[13px] font-medium text-marca"
          >
            <Archive className="size-4" />
            {verArchivados ? 'Ocultar archivados' : `Ver archivados (${archivados.length})`}
          </button>
          {verArchivados && lista(archivados)}
        </section>
      )}
    </div>
  );
}

function AvisoBaseSinAbrir() {
  return (
    <Banner tono="alerta" titulo="No se pudo abrir la base de alimentos">
      Sin ella no se pueden calcular los platillos. Cierra y vuelve a abrir la app.
    </Banner>
  );
}

// ---------------------------------------------------------------------------
// Platillo nuevo o editado
// ---------------------------------------------------------------------------

export function PlatilloPage() {
  const { id } = useParams();
  const existente = useLiveQuery(async () => (id === undefined ? null : ((await leerPlatillo(id)) ?? null)), [id]);
  const [nuevo] = useState(() => {
    const ahora = ahoraIso();
    return { id: nuevoId('pla'), ahora };
  });
  const { base, propios, resolver } = useResolverIngredientes();

  if (existente === undefined || propios === undefined || base.estado === 'cargando') return null;
  if (id !== undefined && existente === null) {
    return (
      <div>
        <PageHeader titulo="No está." volver={{ a: '/comida/platillos', texto: 'Mis platillos' }} />
        <EstadoVacio titulo="Ese platillo ya no existe." descripcion="Lo que registraste con él no cambia." />
      </div>
    );
  }

  const inicial: Dish = existente ?? {
    id: nuevo.id,
    nombre: '',
    raciones: 1,
    ingredientes: [],
    pesoFinalG: null,
    favorito: false,
    archivado: false,
    creadoEn: nuevo.ahora,
    actualizadoEn: nuevo.ahora,
  };
  return (
    <FormularioPlatillo
      key={inicial.id}
      inicial={inicial}
      esNuevo={existente === null}
      base={base}
      propios={propios}
      resolver={resolver}
    />
  );
}

/** Sin separador de miles, para que el campo se pueda editar. */
const texto = (v: number | null) => (v === null ? '' : formatearNumero(v, { decimales: 2, fijos: false }).replace(/,/g, ''));

interface FilaIngrediente {
  clave: string;
  origen: IngredientePlatillo['origen'];
  alimentoId: string;
  cantidad: string;
  porcion: string;
}

interface CandidatoIngrediente extends Candidato {
  origen: IngredientePlatillo['origen'];
  id: string;
  detalle: string;
  estado: EstadoCoccion | null;
}

/** La base y Mis alimentos sin archivar, para el buscador de ingredientes. */
function candidatosDe(base: EstadoBase, propios: readonly Food[]): CandidatoIngrediente[] {
  const deBase = base.estado === 'lista' ? base.modulo.ALIMENTOS_BASE : [];
  return [
    ...propios
      .filter((f) => !f.archivado)
      .map((f) => ({
        clave: `propio:${f.id}`,
        origen: 'propio' as const,
        id: f.id,
        nombre: f.nombre,
        sinonimos: [],
        favorito: f.favorito,
        estado: estadoDe(f),
        detalle:
          f.referencia.tipo === 'por100g'
            ? `${formatearKcal(f.nutrientes.kcal)} por 100 g`
            : `${formatearKcal(f.nutrientes.kcal)} por ${f.referencia.porcion.nombre}`,
      })),
    ...deBase.map((a) => ({
      clave: `base:${a.id}`,
      origen: 'base' as const,
      id: a.id,
      nombre: a.nombre,
      sinonimos: a.sinonimos,
      favorito: false,
      estado: a.estado,
      detalle: `${formatearKcal(a.por100g.kcal)} por 100 g`,
    })),
  ];
}

/** Los ingredientes se ordenan por coincidencia y favoritos, sin frecuencia de uso. */
const SIN_USOS: ReadonlyMap<string, Uso> = new Map();

function FormularioPlatillo({
  inicial,
  esNuevo,
  base,
  propios,
  resolver,
}: {
  inicial: Dish;
  esNuevo: boolean;
  base: EstadoBase;
  propios: readonly Food[];
  resolver: ResolverIngrediente | null;
}) {
  const navegar = useNavigate();
  const hoy = useFechaHoy();
  const [nombre, setNombre] = useState(inicial.nombre);
  const [raciones, setRaciones] = useState(esNuevo ? '' : texto(inicial.raciones));
  const [peso, setPeso] = useState(texto(pesoFinalDe(inicial)));
  const [filas, setFilas] = useState<FilaIngrediente[]>(() =>
    inicial.ingredientes.map((i, n) => ({
      clave: `i${n}`,
      origen: i.origen,
      alimentoId: i.alimentoId,
      cantidad: texto(i.cantidad),
      porcion: i.porcion,
    })),
  );
  /** La fila recién agregada recibe el foco en su cantidad. */
  const [agregada, setAgregada] = useState<string | null>(null);
  const [favorito, setFavorito] = useState(inicial.favorito);
  const [consulta, setConsulta] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmarBorrar, setConfirmarBorrar] = useState(false);

  const candidatos = useMemo(() => candidatosDe(base, propios), [base, propios]);
  const resultados = consulta.trim() === '' ? [] : buscarAlimentos(consulta, candidatos, SIN_USOS, hoy, 12);

  const racionesNumero = leerDecimal(raciones);
  const pesoNumero = peso.trim() === '' ? null : leerDecimal(peso);
  const ingredientes: IngredientePlatillo[] = filas.map((f) => ({
    origen: f.origen,
    alimentoId: f.alimentoId,
    // Vacío o mal escrito: el cálculo lo marca en su fila
    cantidad: leerDecimal(f.cantidad) ?? Number.NaN,
    porcion: f.porcion,
  }));
  const calculo =
    resolver === null || filas.length === 0
      ? null
      : calcularPlatillo({ raciones: racionesNumero ?? 0, ingredientes }, resolver);
  const borrador = { pesoFinalG: pesoNumero };
  const por100g = calculo === null ? null : por100gDePlatillo(borrador, calculo);
  const lleno = filas.length >= MAXIMO_INGREDIENTES;

  function agregar(c: CandidatoIngrediente) {
    if (lleno) return;
    const alimento = resolver?.({ origen: c.origen, alimentoId: c.id, cantidad: 1, porcion: GRAMOS }) ?? null;
    const porcion = alimento === null ? GRAMOS : (opcionesDePorcion(alimento.calculable)[0]?.clave ?? GRAMOS);
    const clave = `n${Date.now()}`;
    setFilas((fs) => [
      ...fs,
      { clave, origen: c.origen, alimentoId: c.id, cantidad: porcion === GRAMOS ? '100' : '1', porcion },
    ]);
    setAgregada(clave);
    setConsulta('');
  }

  function cambiarFila(clave: string, cambios: Partial<FilaIngrediente>) {
    setFilas((fs) => fs.map((f) => (f.clave === clave ? { ...f, ...cambios } : f)));
  }

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    if (nombre.trim() === '') return setError('Escribe el nombre del platillo.');
    if (racionesNumero === null) return setError('Escribe cuántas raciones rinde.');
    if (peso.trim() !== '' && pesoNumero === null) return setError('Revisa el peso final: debe ser un número.');
    if (filas.length === 0) return setError('Agrega al menos un ingrediente.');
    if (resolver === null || calculo === null) {
      return setError('No se pudo abrir la base de alimentos: cierra y vuelve a abrir la app.');
    }
    const primero = calculo.errores[0];
    if (primero !== undefined) return setError(`Revisa los ingredientes. ${primero}`);
    setError(null);
    setGuardando(true);
    try {
      await guardarPlatillo({
        ...inicial,
        nombre,
        raciones: racionesNumero,
        pesoFinalG: pesoNumero,
        ingredientes,
        favorito,
      });
      void navegar('/comida/platillos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function archivar(valor: boolean) {
    setError(null);
    try {
      await archivarPlatillo(inicial.id, valor);
      void navegar('/comida/platillos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function borrar() {
    setError(null);
    try {
      await borrarPlatillo(inicial.id);
      void navegar('/comida/platillos', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div>
      <PageHeader
        titulo={esNuevo ? 'Nuevo platillo.' : 'Tu platillo.'}
        descripcion="Sus valores salen de los ingredientes como están hoy. Lo que ya registraste no cambia si después editas el platillo o un ingrediente."
        volver={{ a: '/comida/platillos', texto: 'Mis platillos', etiqueta: 'Volver a Mis platillos' }}
      >
        {inicial.archivado && <Etiqueta tono="aviso">Archivado</Etiqueta>}
      </PageHeader>

      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        <TextField etiqueta="Nombre" valor={nombre} onCambio={setNombre} maxLength={80} placeholder="Caldo de pollo" />
        {/* Abajo alineados: la etiqueta del peso ocupa dos líneas en 320 px */}
        <div className="grid grid-cols-2 items-end gap-3">
          <NumberField etiqueta="Raciones que rinde" valor={raciones} onCambio={setRaciones} placeholder="4" />
          <NumberField etiqueta="Peso final cocinado (opcional)" valor={peso} onCambio={setPeso} placeholder="g" />
        </div>
        <p className="-mt-2 text-[12px] leading-relaxed text-texto-suave">
          Pesa todo el platillo ya cocinado, en gramos, para registrarlo también por gramos. Sin el peso, se
          registra por ración.
        </p>

        {base.estado === 'error' && <AvisoBaseSinAbrir />}

        <section aria-labelledby="titulo-ingredientes" className="grid gap-3">
          <h2 id="titulo-ingredientes" className="antetitulo">
            Ingredientes
          </h2>
          {filas.length === 0 ? (
            <p className="text-[13px] leading-relaxed text-texto-suave">
              Búscalos abajo, en la base o en Mis alimentos, con la cantidad que lleva todo el platillo.
            </p>
          ) : (
            <ul className="divide-y divide-borde">
              {filas.map((f) => (
                <FilaDeIngrediente
                  key={f.clave}
                  fila={f}
                  resolver={resolver}
                  enfocar={f.clave === agregada}
                  onCambio={(cambios) => cambiarFila(f.clave, cambios)}
                  onQuitar={() => setFilas((fs) => fs.filter((x) => x.clave !== f.clave))}
                />
              ))}
            </ul>
          )}

          <label htmlFor="buscar-ingrediente" className="block text-[13px] text-texto-suave">
            Buscar ingrediente
          </label>
          <div className="relative -mt-1.5">
            <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-texto-suave" />
            <input
              id="buscar-ingrediente"
              type="search"
              value={consulta}
              onChange={(e) => setConsulta(e.target.value)}
              placeholder="Pollo, arroz, aceite…"
              autoComplete="off"
              enterKeyHint="search"
              disabled={lleno}
              aria-describedby={lleno ? 'ingredientes-llenos' : undefined}
              className="min-h-12 w-full rounded-[13px] border border-borde-fuerte bg-superficie py-[11px] pl-10 pr-3 text-base text-texto placeholder:text-texto-suave disabled:bg-superficie-suave"
            />
          </div>
          {lleno && (
            <p id="ingredientes-llenos" className="text-[12px] text-texto-suave">
              Un platillo puede tener hasta {MAXIMO_INGREDIENTES} ingredientes.
            </p>
          )}
          {consulta.trim() !== '' &&
            (resultados.length === 0 ? (
              <p className="text-[13px] leading-relaxed text-texto-suave" aria-live="polite">
                “{consulta.trim()}” no está en la base ni en tus alimentos. Captúralo primero en Mis alimentos.
              </p>
            ) : (
              <ul className="divide-y divide-borde" aria-label="Ingredientes encontrados">
                {resultados.map((c) => (
                  <li key={c.clave}>
                    <button
                      type="button"
                      onClick={() => agregar(c)}
                      className="flex min-h-14 w-full items-center gap-3 py-2.5 text-left active:opacity-60"
                    >
                      <span className="grid min-w-0 flex-1 gap-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[14px] font-medium text-texto">{c.nombre}</span>
                          <EtiquetaEstado estado={c.estado} />
                          {c.origen === 'propio' && <Etiqueta>Mío</Etiqueta>}
                        </span>
                        <span className="text-[12px] text-texto-suave">{c.detalle}</span>
                      </span>
                      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-marca-suave text-marca">
                        <Plus aria-hidden="true" className="size-4" />
                      </span>
                      <span className="sr-only">Agregar</span>
                    </button>
                  </li>
                ))}
              </ul>
            ))}
        </section>

        <ValoresDelPlatillo
          calculo={calculo}
          raciones={racionesNumero}
          pesoFinalG={pesoFinalDe(borrador)}
          por100g={por100g}
          hayIngredientes={filas.length > 0}
        />

        <Casilla marcada={favorito} onCambio={setFavorito}>
          Favorito: aparece primero al buscar
        </Casilla>

        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}

        <div className="space-y-2">
          <Button type="submit" icono={Check} disabled={guardando}>
            {guardando ? 'Guardando…' : esNuevo ? 'Guardar platillo' : 'Guardar cambios'}
          </Button>
          {!esNuevo && (
            <>
              <Button
                variante="secundario"
                icono={inicial.archivado ? ArchiveRestore : Archive}
                onClick={() => void archivar(!inicial.archivado)}
              >
                {inicial.archivado ? 'Sacar del archivo' : 'Archivar'}
              </Button>
              {confirmarBorrar ? (
                <div className="space-y-2 rounded-panel bg-alerta-suave p-4">
                  <p className="text-[13px] text-texto">¿Borrar este platillo? Lo que ya registraste con él no cambia.</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variante="peligro" onClick={() => void borrar()}>
                      Sí, borrar
                    </Button>
                    <Button variante="secundario" onClick={() => setConfirmarBorrar(false)}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variante="fantasma" icono={Trash2} onClick={() => setConfirmarBorrar(true)}>
                  Borrar
                </Button>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
}

function FilaDeIngrediente({
  fila,
  resolver,
  enfocar,
  onCambio,
  onQuitar,
}: {
  fila: FilaIngrediente;
  resolver: ResolverIngrediente | null;
  enfocar: boolean;
  onCambio: (cambios: Partial<FilaIngrediente>) => void;
  onQuitar: () => void;
}) {
  const cantidad = leerDecimal(fila.cantidad);
  const alimento =
    resolver?.({ origen: fila.origen, alimentoId: fila.alimentoId, cantidad: cantidad ?? 0, porcion: fila.porcion }) ?? null;
  const nombre = alimento?.nombre ?? 'Ingrediente que ya no existe';
  const opciones = alimento === null ? [] : opcionesDePorcion(alimento.calculable);
  // Si la porción guardada ya no existe en el alimento, se muestra igual y el cálculo pide elegir otra
  const porcionPerdida = alimento !== null && !opciones.some((o) => o.clave === fila.porcion);
  const errorCantidad = cantidad === null || !(cantidad > 0);
  const resultado =
    alimento === null ? null : calcular(alimento.calculable, cantidad ?? Number.NaN, fila.porcion);
  const idDetalle = `detalle-${fila.clave}`;

  return (
    <li className="grid gap-2 py-3">
      <div className="flex items-start gap-2">
        <span className="grid min-w-0 flex-1 gap-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-[14px] font-medium text-texto">{nombre}</span>
            {fila.origen === 'propio' && alimento !== null && <Etiqueta>Mío</Etiqueta>}
          </span>
          {resultado === null ? (
            <span id={idDetalle} className="text-[12px] font-medium text-alerta">
              {resolver === null
                ? 'Sin la base de alimentos no se puede calcular.'
                : 'Ya no está en la base ni en Mis alimentos: quítalo.'}
            </span>
          ) : resultado.ok ? (
            <span id={idDetalle} className="text-[12px] tabular-nums text-texto-suave">
              {[formatearKcal(resultado.valores.kcal), resultado.gramos === null ? null : formatearGramos(resultado.gramos)]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : (
            <span id={idDetalle} className="text-[12px] font-medium text-alerta">
              {resultado.error}
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={onQuitar}
          className="grid size-11 shrink-0 place-items-center rounded-full bg-marca-suave text-texto"
        >
          <X aria-hidden="true" className="size-4" />
          <span className="sr-only">Quitar {nombre}</span>
        </button>
      </div>
      {alimento !== null && (
        <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2">
          <input
            aria-label={`Cantidad de ${nombre}`}
            aria-describedby={idDetalle}
            aria-invalid={errorCantidad}
            type="text"
            inputMode="decimal"
            value={fila.cantidad}
            onChange={(e) => onCambio({ cantidad: limpiarDecimal(e.target.value) })}
            autoFocus={enfocar}
            autoComplete="off"
            className={`${clasesCon(errorCantidad)} text-right tabular-nums`}
          />
          <select
            aria-label={`Porción de ${nombre}`}
            value={fila.porcion}
            onChange={(e) => onCambio({ porcion: e.target.value })}
            className={clasesCon(porcionPerdida)}
          >
            {porcionPerdida && (
              <option value={fila.porcion} disabled>
                {fila.porcion} (ya no existe)
              </option>
            )}
            {opciones.map((o) => (
              <option key={o.clave} value={o.clave}>
                {o.etiqueta}
              </option>
            ))}
          </select>
        </div>
      )}
    </li>
  );
}

/** Por ración (o todo el platillo mientras faltan las raciones), con el peso final si lo hay. */
function ValoresDelPlatillo({
  calculo,
  raciones,
  pesoFinalG,
  por100g,
  hayIngredientes,
}: {
  calculo: CalculoPlatillo | null;
  raciones: number | null;
  pesoFinalG: number | null;
  por100g: Nutrientes | null;
  hayIngredientes: boolean;
}) {
  const total = calculo?.total ?? null;
  if (calculo === null || total === null) {
    return (
      <p className="rounded-panel bg-superficie p-5 text-[13px] leading-relaxed text-texto-suave" aria-live="polite">
        {hayIngredientes ? 'Revisa los ingredientes marcados para ver sus valores.' : 'Agrega los ingredientes para ver sus valores.'}
      </p>
    );
  }
  // porRacion es null mientras las raciones no son un número mayor que cero
  const { porRacion, gramosIngredientes } = calculo;
  const principal = porRacion ?? total;
  const macro = (titulo: string, g: number | null) => (
    <div className="min-w-0">
      <dt className="text-[12px] text-texto-suave">{titulo}</dt>
      <dd className="text-[15px] font-medium tabular-nums text-texto">{g === null ? 'Sin dato' : formatearGramos(g)}</dd>
    </div>
  );
  const notas = [
    porRacion !== null
      ? `Todo el platillo: ${formatearKcal(total.kcal)} · ${textoMacros(total)}.`
      : 'Escribe las raciones para verlo por ración.',
    por100g === null ? null : `Por 100 g cocinado: ${formatearKcal(por100g.kcal)}.`,
    gramosIngredientes === null ? null : `Los ingredientes suman ${formatearGramos(gramosIngredientes)}.`,
  ].filter((n): n is string => n !== null);

  return (
    <div className="rounded-panel bg-superficie p-5" aria-live="polite">
      <span className="antetitulo">{porRacion !== null ? 'Por ración' : 'Todo el platillo'}</span>
      <p className="mt-2 text-[30px] leading-none tracking-[-1px] tabular-nums text-texto">{formatearKcal(principal.kcal)}</p>
      {porRacion !== null && pesoFinalG !== null && raciones !== null && (
        <p className="mt-1.5 text-[12px] text-texto-suave">{formatearGramos(pesoFinalG / raciones)} por ración</p>
      )}
      <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-3 min-[400px]:grid-cols-4">
        {macro('Proteína', principal.proteinaG)}
        {macro('Carbohidratos', principal.carbohidratosG)}
        {macro('Grasa', principal.grasaG)}
        {macro('Fibra', principal.fibraG)}
      </dl>
      <div className="mt-3 grid gap-1 text-[12px] leading-relaxed text-texto-suave">
        {notas.map((n) => (
          <p key={n}>{n}</p>
        ))}
      </div>
    </div>
  );
}
